import thaprachanMenu, { ThaprachanMenuItem } from './thaprachanMenu';

/**
 * Landing pages read their dishes out of the live menu rather than restating
 * them, so a menu edit updates every page that mentions the dish. If an id ever
 * disappears from the menu it drops off the landing page instead of leaving
 * stale copy behind.
 */
export interface LookedUpItem extends ThaprachanMenuItem {
  section: string;
}

const index = new Map<string, LookedUpItem>();
for (const section of thaprachanMenu) {
  for (const item of section.items) {
    index.set(item.id, { ...item, section: section.title });
  }
}

export const getMenuItems = (ids: string[]): LookedUpItem[] =>
  ids.map((id) => index.get(id)).filter((item): item is LookedUpItem => Boolean(item));

export const getMenuSection = (title: string): LookedUpItem[] => {
  const section = thaprachanMenu.find((s) => s.title === title);
  if (!section) return [];
  return section.items.map((item) => ({ ...item, section: section.title }));
};

/**
 * Menu sections rendered into the DOM on every visit, regardless of which tab
 * is active.
 *
 * The menu is a tabbed interface that mounts only the active section, so a
 * crawler that never clicks sees one section out of eleven. These four carry
 * the search value — they hold every dish that has a page of its own, plus the
 * curries — so they are always present in the document and merely hidden with
 * CSS when another tab is selected. Google indexes DOM content hidden behind
 * tabs; it cannot index content that was never rendered.
 *
 * The remaining sections still mount on demand, exactly as before.
 *
 * MenuPage builds its structured data from this same list, so the markup can
 * never describe sections the page did not render. Add a section here and both
 * the crawlable HTML and the schema pick it up.
 */
export const CRAWLABLE_SECTIONS = [
  'Noodle Dishes',
  'Curry',
  'Street Food',
  'Desserts',
] as const;
